import { useState, useEffect, useRef } from 'react';
import { searchCustomers, findOrCreateCustomer } from '../api/customers.api.js';
import { useCartStore } from '../store/cartStore.js';
import { IcoClose, IcoSearch } from './common/Icons.jsx';
import styles from './CustomerPickerModal.module.css';

export default function CustomerPickerModal({ onClose }) {
  const setCustomer  = useCartStore((s) => s.setCustomer);
  const clearCustomer = useCartStore((s) => s.clearCustomer);
  const currentCustomer = useCartStore((s) => s.customer);

  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState('search'); // 'search' | 'create'
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (mode !== 'search') return;
    clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchCustomers(query.trim());
        setResults(res.data?.customers ?? res.data ?? []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, mode]);

  function select(customer) {
    setCustomer(customer);
    onClose();
  }

  function handleRemove() {
    clearCustomer();
    onClose();
  }

  async function handleCreate() {
    if (!form.name.trim()) { setCreateError('El nombre es obligatorio'); return; }
    setCreateError(null);
    setCreating(true);
    try {
      const res = await findOrCreateCustomer({
        name:  form.name.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
      });
      const customer = res.data?.customer ?? res.data;
      setCustomer(customer);
      onClose();
    } catch (err) {
      setCreateError(err.response?.data?.error ?? 'Error al crear cliente');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>
            {mode === 'search' ? 'Seleccionar cliente' : 'Nuevo cliente'}
          </span>
          <button className={styles.closeBtn} onClick={onClose}><IcoClose size={13} /></button>
        </div>

        {mode === 'search' ? (
          <>
            {/* Search input */}
            <div className={styles.searchWrap}>
              <input
                ref={inputRef}
                className={styles.searchInput}
                placeholder="Buscar por nombre, teléfono o email…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {loading && <span className={styles.spinner} />}
            </div>

            {/* Results */}
            <div className={styles.results}>
              {currentCustomer && (
                <button className={styles.removeBtn} onClick={handleRemove}>
                  Quitar cliente asignado
                </button>
              )}

              {results.length > 0 ? (
                results.map((c) => (
                  <button key={c._id} className={styles.resultRow} onClick={() => select(c)}>
                    <span className={styles.resultName}>{c.name}</span>
                    <span className={styles.resultSub}>
                      {[c.phone, c.email].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                ))
              ) : query.trim() && !loading ? (
                <p className={styles.noResults}>Sin resultados para "{query}"</p>
              ) : null}
            </div>

            {/* Footer */}
            <div className={styles.footer}>
              <button className={styles.newBtn} onClick={() => { setMode('create'); setCreateError(null); }}>
                + Crear nuevo cliente
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Create form */}
            <div className={styles.form}>
              <label className={styles.label}>Nombre <span className={styles.req}>*</span></label>
              <input
                className={styles.input}
                placeholder="Nombre completo"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                autoFocus
              />

              <label className={styles.label}>Teléfono</label>
              <input
                className={styles.input}
                placeholder="10 dígitos"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />

              <label className={styles.label}>Email</label>
              <input
                className={styles.input}
                placeholder="correo@ejemplo.com"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />

              {createError && <p className={styles.error}>{createError}</p>}
            </div>

            <div className={styles.footer}>
              <button className={styles.backBtn} onClick={() => setMode('search')}>
                ← Volver
              </button>
              <button
                className={styles.createBtn}
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? 'Guardando…' : 'Guardar cliente'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
